import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ShopInfo, ShopSettings } from '../types';
import { getSessionToken } from '../hooks/useApi';

interface Analytics {
  totalProductViews: number;
  totalAddToCarts: number;
  totalPurchases: number;
  conversionRate: number;
}

interface SetupStatus {
  isInstalled: boolean;
  isComplete: boolean;
  message: string;
  hasAccessToken: boolean;
  hasBehaviorData: boolean;
  hasTrainedModel: boolean;
  hasProducts: boolean;
  hasProductAssociations: boolean;
  totalBehaviors: number;
  totalAssociations: number;
  productCount: number;
  lastBehaviorAt: string | null;
}

interface ThemeStatus {
  hasRecommendationsBlock: boolean;
  hasBundlesBlock: boolean;
}

interface ShopContextType {
  shop: string | null;
  shopInfo: ShopInfo | null;
  settings: ShopSettings | null;
  analytics: Analytics | null;
  setupStatus: SetupStatus | null;
  themeStatus: ThemeStatus | null;
  isLoading: boolean;
  isLoadingAnalytics: boolean;
  isLoadingSetupStatus: boolean;
  isLoadingThemeStatus: boolean;
  error: string | null;
  refreshShopInfo: () => Promise<void>;
  refreshSettings: () => Promise<void>;
  refreshAnalytics: () => Promise<void>;
  refreshSetupStatus: () => Promise<void>;
}

const ShopContext = createContext<ShopContextType | undefined>(undefined);

export function ShopProvider({ children }: { children: ReactNode }) {
  const [shop, setShop] = useState<string | null>(null);
  const [shopInfo, setShopInfo] = useState<ShopInfo | null>(null);
  const [settings, setSettings] = useState<ShopSettings | null>(null);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [setupStatus, setSetupStatus] = useState<SetupStatus | null>(null);
  const [themeStatus, setThemeStatus] = useState<ThemeStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [isLoadingSetupStatus, setIsLoadingSetupStatus] = useState(false);
  const [isLoadingThemeStatus, setIsLoadingThemeStatus] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Get shop from URL query params, fallback to sessionStorage
    const params = new URLSearchParams(window.location.search);
    const shopDomain = params.get('shop');
    if (shopDomain) {
      setShop(shopDomain);
      sessionStorage.setItem('nexus_shop_domain', shopDomain);
    } else {
      const stored = sessionStorage.getItem('nexus_shop_domain');
      if (stored) {
        setShop(stored);
      }
    }
  }, []);

  useEffect(() => {
    if (shop) {
      refreshShopInfo();
      refreshSettings();
      // Load analytics and setup status in background (don't block initial render)
      refreshAnalytics();
      refreshSetupStatus();
      refreshThemeStatus();
    }
  }, [shop]);

  const refreshShopInfo = async () => {
    if (!shop) return;
    
    try {
      setIsLoading(true);
      const headers: Record<string, string> = { 'ngrok-skip-browser-warning': 'true' };
      const token = await getSessionToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch('/api/shopify/shop', {
        headers
      });
      if (response.ok) {
        const data = await response.json();
        setShopInfo(data);
      } else {
        setError('Failed to fetch shop info');
      }
    } catch (err) {
      setError('Network error');
      console.error('Error fetching shop info:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSettings = async () => {
    if (!shop) return;
    
    try {
      // Load settings WITHOUT analytics for fast initial load
      const headers: Record<string, string> = { 'ngrok-skip-browser-warning': 'true' };
      const token = await getSessionToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const url = '/api/shopify/settings?includeAnalytics=false';
      console.log('[ShopContext] Fetching settings:', url, 'hasToken:', !!token);
      const response = await fetch(url, { headers });
      console.log('[ShopContext] Settings response:', response.status, response.statusText);
      if (response.ok) {
        const data = await response.json();
        console.log('[ShopContext] Settings loaded:', data ? 'OK' : 'null');
        setSettings(data);
      } else {
        const errorText = await response.text().catch(() => '');
        console.error('[ShopContext] Settings failed:', response.status, errorText);
      }
    } catch (err) {
      console.error('[ShopContext] Error fetching settings:', err);
    }
  };

  const refreshAnalytics = async () => {
    if (!shop) return;
    
    try {
      setIsLoadingAnalytics(true);
      const headers: Record<string, string> = { 'ngrok-skip-browser-warning': 'true' };
      const token = await getSessionToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch('/api/shopify/analytics', {
        headers
      });
      if (response.ok) {
        const data = await response.json();
        // API returns { analytics: { ... } }, extract the inner object
        setAnalytics(data.analytics ?? data);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  const refreshSetupStatus = async () => {
    if (!shop) return;
    
    try {
      setIsLoadingSetupStatus(true);
      
      // Stale-while-revalidate: show cached data immediately, then fetch fresh
      const cacheKey = `nexus_setup_status:${shop}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try { setSetupStatus(JSON.parse(cached)); } catch {}
      }
      
      const headers: Record<string, string> = { 'ngrok-skip-browser-warning': 'true' };
      const token = await getSessionToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch('/api/shopify/setup-status', {
        headers
      });
      if (response.ok) {
        const data = await response.json();
        setSetupStatus(data);
        // Only cache when ALL steps are active. If any step is inactive,
        // don't cache so merchant sees changes immediately on next load.
        if (data.isComplete && data.hasProducts && data.hasTrainedModel && data.hasProductAssociations) {
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
        } else {
          sessionStorage.removeItem(cacheKey);
        }
      }
    } catch (err) {
      console.error('Error fetching setup status:', err);
    } finally {
      setIsLoadingSetupStatus(false);
    }
  };

  const refreshThemeStatus = async () => {
    if (!shop) return;
    
    try {
      setIsLoadingThemeStatus(true);
      
      // Stale-while-revalidate: show cached data immediately, then fetch fresh
      const cacheKey = `nexus_theme_status:${shop}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try { setThemeStatus(JSON.parse(cached)); } catch {}
      }
      
      const headers: Record<string, string> = { 'ngrok-skip-browser-warning': 'true' };
      const token = await getSessionToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch('/api/shopify/theme-status', {
        headers
      });
      if (response.ok) {
        const data = await response.json();
        setThemeStatus(data);
        // Only cache when ALL theme blocks are active. If any block is inactive,
        // don't cache so merchant sees changes immediately when toggling theme editor.
        if (data.hasRecommendationsBlock && data.hasBundlesBlock) {
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
        } else {
          sessionStorage.removeItem(cacheKey);
        }
      }
    } catch (err) {
      console.error('Error fetching theme status:', err);
    } finally {
      setIsLoadingThemeStatus(false);
    }
  };

  return (
    <ShopContext.Provider
      value={{
        shop,
        shopInfo,
        settings,
        analytics,
        setupStatus,
        themeStatus,
        isLoading,
        isLoadingAnalytics,
        isLoadingSetupStatus,
        isLoadingThemeStatus,
        error,
        refreshShopInfo,
        refreshSettings,
        refreshAnalytics,
        refreshSetupStatus,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
}

export function useShop() {
  const context = useContext(ShopContext);
  if (context === undefined) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
}
